// src/lib/horaires-decision.js
//
// LES RÈGLES DE L'ÉCRAN « DEMANDES D'HORAIRES » — helpers PURS. Aucun accès
// réseau, aucun DOM, aucune écriture. Le rendu vit dans
// src/views/OrchestrateurHorairesView.jsx ; les RÈGLES vivent ici, parce qu'une
// règle qui n'existe que dans du JSX ne peut être ni mesurée ni cassée exprès.
//
// ⚠️ CET ÉCRAN NE CONSTATE PAS, IL SERT À DÉCIDER. La question unique est :
// « combien de temps reste-t-il pour le ménage si j'accepte ? » Tout ce module
// existe pour répondre à cette question, EN DIRECT, pendant qu'on règle l'heure.
//
// ⚠️ ON GROUPE PAR JOURNÉE DE LOGEMENT, PAS PAR DEMANDE. Deux demandes le même
// jour sur le même logement se décident ENSEMBLE : accorder l'une change ce qui
// reste possible pour l'autre. Les traiter séparément, c'est décider deux fois
// avec un chiffre faux la seconde.
//
// ⚠️ AUCUN MONTANT N'ENTRE ICI, NI N'EN SORT. L'orchestrateur décide sur le
// temps de ménage. Le mécanisme payant existe (capture partielle Stripe,
// lib/services-grant.js) et reste entièrement SERVEUR : le montant n'est ni
// affiché, ni transmis, ni dérivable de ce module.

import {
  dureeDuMenage, formaterHeureCourte, formaterDuree, DUREE_DEFAUT_MIN, PAS_AIMANT_MIN,
} from './menage-frise'
import { parseHHMMToMinutes, formatMinutesToHHMM } from './horaires-impact'

/** Bornes de l'axe de CET écran. Plus serré que la frise : on regarde UN ménage. */
export const AXE_DEBUT_MIN = 9 * 60
export const AXE_FIN_MIN = 17 * 60
export const AXE_AMPLITUDE_MIN = AXE_FIN_MIN - AXE_DEBUT_MIN
export const HEURES_GRADUEES = [9, 11, 13, 15, 17]

/** Pas des curseurs. La demi-heure est l'unité dont on parle sur le terrain. */
// ⚠️ L'UNITÉ REDEVIENT LA DEMI-HEURE (décision Dimitri 2026-08-17, qui ANNULE
// explicitement sa correction du 2026-08-15) : le voyageur est facturé à la
// DEMI-heure entamée (billableHours serveur, pas de 30 min). Le gain minimal
// accordable est 30 min.
export const GAIN_MIN_MINUTES = 30

export const LATE = 'late_checkout'
export const EARLY = 'early_checkin'

/** Position d'un instant sur l'axe 9 h → 17 h, en %. Clampée aux bornes. */
export function pct(min) {
  if (!Number.isFinite(min)) return null
  const borne = Math.max(AXE_DEBUT_MIN, Math.min(AXE_FIN_MIN, min))
  return ((borne - AXE_DEBUT_MIN) / AXE_AMPLITUDE_MIN) * 100
}

/** Largeur en % entre deux instants. Jamais négative. */
export function largeur(debut, fin) {
  const a = pct(debut)
  const b = pct(fin)
  if (a == null || b == null) return 0
  return Math.max(0, b - a)
}

// ── LE SEUIL — la durée de CE ménage-là, jamais un nombre fixe ──────────────

/**
 * COMBIEN DE TEMPS IL FAUT POUR CE MÉNAGE.
 *
 * ⚠️ CE N'EST PAS 90 MIN PARTOUT. C'est, dans l'ordre : l'exception posée sur
 * CE ménage (`menage_runs.duree_min`), puis le défaut du logement
 * (`menage_duree_min`), puis 90. Exactement la règle posée le 2026-08-10 dans
 * `menage-frise.js::dureeDuMenage` — on la RÉUTILISE plutôt que d'en écrire une
 * seconde. Deux implémentations du même seuil finiraient par diverger, et
 * l'écran de décision autoriserait ce que la frise refuse.
 */
export function seuilMenage(run, logement) {
  return dureeDuMenage(run, logement)
}

// ── LES DEUX HEURES DE RÉFÉRENCE, ET ELLES DIFFÈRENT ────────────────────────

/**
 * ⚠️ LE CALCUL SE FAIT SUR LE CONTRAT, JAMAIS SUR L'ANNONCÉ.
 *
 * Un voyageur qui annonce 19 h au formulaire de bienvenue peut arriver à 17 h :
 * c'est son droit, l'horaire du contrat est le sien. Calculer la marge sur
 * 19 h mettrait l'agent en risque le jour où le voyageur exerce ce droit — et
 * ce jour-là, personne ne saurait d'où vient le retard.
 *
 * L'heure annoncée reste une INFORMATION précieuse (« vous avez sans doute plus
 * de marge que le contrat ne le dit »), affichée comme telle et jamais entrée
 * dans un calcul.
 *
 * @returns {{debut:number, fin:number}|null} les bornes CONTRACTUELLES du ménage
 */
export function bornesContrat(logement) {
  const debut = parseHHMMToMinutes(logement?.checkout)
  const fin = parseHHMMToMinutes(logement?.checkin)
  if (debut == null || fin == null) return null
  return { debut, fin }
}

// ── LA SIMULATION ───────────────────────────────────────────────────────────

/**
 * CE QUI RESTERAIT POUR LE MÉNAGE si on accordait les heures proposées.
 *
 * `decisions` = { [idDemande]: { heure: number|null, refusee: boolean } }.
 * Une demande REFUSÉE rend sa borne au contrat — c'est tout le sens du refus,
 * et c'est ce qui libère l'autre curseur.
 *
 * @returns {{debut:number, fin:number, restantMin:number}|null}
 */
export function simuler({ contrat, demandes, decisions = {} }) {
  if (!contrat) return null
  let debut = contrat.debut
  let fin = contrat.fin
  for (const d of Array.isArray(demandes) ? demandes : []) {
    const dec = decisions[d.id]
    if (!dec || dec.refusee || !Number.isFinite(dec.heure)) continue
    if (d.event_kind === LATE) debut = dec.heure
    else if (d.event_kind === EARLY) fin = dec.heure
  }
  return { debut, fin, restantMin: fin - debut }
}

/**
 * LE VERDICT — le sujet de l'écran, en un mot.
 *
 * Quatre états et non deux : « pile au seuil » n'est pas « confortable », et
 * « zéro » n'est pas « un peu court ». Les fondre rendrait la même couleur à
 * une journée tenable et à une journée impossible.
 *
 * @returns {'suffisant'|'juste'|'insuffisant'|'nul'}
 */
export function verdict(restantMin, seuilMin) {
  if (!Number.isFinite(restantMin) || restantMin <= 0) return 'nul'
  if (!Number.isFinite(seuilMin) || seuilMin <= 0) return 'suffisant'
  if (restantMin > seuilMin) return 'suffisant'
  if (restantMin === seuilMin) return 'juste'
  return 'insuffisant'
}

/** La note sous le nombre — elle dit ce qu'on en pense, pas ce qu'il vaut. */
export function noteVerdict(v, seuilMin) {
  const il = `il en faut ${formaterDuree(seuilMin)}`
  return {
    suffisant: 'de quoi travailler sereinement',
    juste: 'tout juste le minimum',
    insuffisant: `${il} · trop serré`,
    nul: `${il} · impossible en l'état`,
  }[v] || ''
}

// ── LES BORNES DU CURSEUR ───────────────────────────────────────────────────

/**
 * JUSQU'OÙ LE CURSEUR PEUT ALLER.
 *
 * ⚠️ IL NE DESCEND JAMAIS SOUS LE SEUIL DE MÉNAGE. Laisser glisser vers une
 * valeur qu'on refusera ensuite est une fausse liberté : on donne l'impression
 * de pouvoir accorder, puis on retire. La butée dit la contrainte au moment où
 * on la rencontre, ce qu'aucun message d'erreur ne fait aussi bien.
 *
 * ⚠️ IL N'ATTEINT PAS NON PLUS L'HEURE DU CONTRAT. Accorder l'heure du contrat,
 * c'est REFUSER en disant « accordé » : côté serveur, zéro heure facturable
 * déclenche l'annulation de l'empreinte, le voyageur reçoit « demande non
 * retenue », et l'historique la liste « refusée » — trois versions
 * contradictoires de la même action (défaut B2, revue adversariale
 * 2026-08-06). Le curseur s'arrête donc un pas avant, et « Refuser » reste le
 * chemin qui dit ce qu'il fait.
 *
 * ⚠️ LES BORNES SE CALCULENT SUR LE CONTRAT, PAS SUR L'AUTRE CURSEUR.
 *
 * Première version : chaque curseur était borné par la position COURANTE de
 * l'autre, de sorte qu'un état infaisable devenait impossible à atteindre. Ça
 * paraissait plus sûr — c'était l'inverse. Deux voyageurs demandent 14 h et
 * 14 h 30 ; l'écran ramenait silencieusement les curseurs à un compromis
 * faisable et affichait un verdict VERT. L'orchestrateur ne voyait jamais que
 * ce qu'on lui demandait était impossible : l'écran avait arbitré à sa place.
 *
 * Chaque curseur garantit donc « si l'autre borne reste au contrat, le ménage
 * tient ». L'infaisabilité CONJOINTE, elle, se voit — c'est le rôle du verdict
 * qui passe au rouge et du bandeau « aucun créneau possible ». Montrer le
 * problème est tout l'objet de cet écran ; l'empêcher d'exister le supprimerait
 * de la vue sans le supprimer de la réalité.
 *
 * @returns {{min:number, max:number, possible:boolean}}
 */
export function bornesCurseur({ demande, contrat, seuilMin, pas = GAIN_MIN_MINUTES }) {
  const requis = parseHHMMToMinutes(demande?.requested_time)
  if (!contrat || requis == null) return { min: 0, max: 0, possible: false }

  if (demande.event_kind === LATE) {
    // Le partant s'en va plus tard : le ménage COMMENCE plus tard.
    const min = contrat.debut + pas          // jamais l'heure du contrat elle-même
    const max = Math.min(requis, contrat.fin - seuilMin)
    return { min, max, possible: max >= min }
  }
  // EARLY : le suivant arrive plus tôt, le ménage doit FINIR plus tôt.
  const max = contrat.fin - pas
  const min = Math.max(requis, contrat.debut + seuilMin)
  return { min, max, possible: max >= min }
}

/**
 * LES PASTILLES D'HEURES (règle corrigée Dimitri 2026-08-15) — des heures
 * ACCORDÉES uniquement : l'heure du contrat n'y est PAS (l'accorder serait un
 * refus, et « Refuser » existe). LA RÈGLE EST MÉTIER : le voyageur est facturé
 * À L'HEURE — les pastilles sont les GAINS D'HEURES ENTIÈRES entre le contrat
 * et la demande (contrat 11h, demande 15h → 12h, 13h, 14h, 15h). La dernière
 * est TOUJOURS la demandée, marquée. Écart > 5 h : les 4 dernières avant la
 * demande, jamais huit. Une demandée hors grille (données inattendues) reste
 * en dernière position.
 *
 * « Cette heure tient-elle ? » reste l'affaire de simuler() + verdict().
 *
 * @returns {{ pastilles: Array<{heure:number, tag:'demande'|null}> }}
 */
export function heuresPastilles({ demande, contrat }) {
  const requis = parseHHMMToMinutes(demande?.requested_time)
  if (!contrat || requis == null) return { pastilles: [] }

  const late = demande.event_kind === LATE
  const contratMin = late ? contrat.debut : contrat.fin
  const sens = late ? 1 : -1
  const ecart = (requis - contratMin) * sens
  if (ecart <= 0) return { pastilles: [] }

  const heures = []
  for (let k = 60; k <= ecart; k += 60) heures.push(contratMin + k * sens)
  // La demandée est TOUJOURS la dernière — même hors grille d'heures entières.
  if (!heures.includes(requis)) heures.push(requis)

  let liste = heures.map((h) => ({ heure: h, tag: h === requis ? 'demande' : null }))
  if (liste.length > 4) liste = liste.slice(-4)
  // Affichage chronologique (une arrivée anticipée se lit aussi de gauche à droite).
  liste.sort((a, b) => a.heure - b.heure)
  return { pastilles: liste }
}

/** Aimante une valeur de curseur au pas, en restant dans ses bornes. */
export function aimanterDansBornes(valeur, { min, max }, pas = 1) {
  if (!Number.isFinite(valeur)) return min
  const arrondi = pas > 1 ? Math.round(valeur / pas) * pas : Math.round(valeur)
  return Math.max(min, Math.min(max, arrondi))
}

/**
 * L'heure de départ d'un curseur : ce que le voyageur a DEMANDÉ, ramené dans
 * les bornes. On part de sa demande parce que c'est la décision par défaut la
 * plus probable — et parce que partir de l'heure du contrat afficherait
 * d'emblée « aucun gain », ce qui n'est pas ce que l'écran a à dire.
 */
export function heureInitiale(demande, bornes) {
  const requis = parseHHMMToMinutes(demande?.requested_time)
  if (requis == null) return bornes.min
  return aimanterDansBornes(requis, bornes)
}

// ── CE QUE CHAQUE VOYAGEUR GAGNE ────────────────────────────────────────────

/**
 * « Camille gagne 2 h (contrat 11h) » — le rappel sous les curseurs.
 *
 * Gain compté PAR RAPPORT AU CONTRAT, jamais par rapport à ce qu'il a demandé :
 * « Camille perd 1 h » serait vrai vis-à-vis de sa demande et absurde vis-à-vis
 * de son droit. On ne rend pas compte d'un refus partiel, on dit ce qu'il
 * obtient de plus.
 */
export function libelleGain(demande, heureMin, contrat) {
  if (!demande || !contrat) return null
  const ref = demande.event_kind === LATE ? contrat.debut : contrat.fin
  const gain = demande.event_kind === LATE ? heureMin - ref : ref - heureMin
  const qui = demande.voyageur || 'Le voyageur'
  if (!Number.isFinite(gain) || gain <= 0) {
    return demande.event_kind === LATE
      ? `${qui} part à l'heure du contrat`
      : `${qui} arrive à l'heure du contrat`
  }
  // « 3 h de plus que prévu » — l'ancien « gagne 3 h (contrat 11h) » était
  // lourd (correction Dimitri 2026-08-15).
  return `${qui} · ${formaterDuree(gain)} de plus que prévu`
}

/**
 * L'HEURE ANNONCÉE AU FORMULAIRE — information, jamais calcul.
 *
 * Elle ne sert qu'à dire à l'orchestrateur qu'il a probablement plus de marge
 * que le contrat ne l'indique. Elle n'entre dans aucune borne, aucun verdict.
 */
export function libelleAnnonce(demande, annonceHHMM, contrat) {
  const annonce = parseHHMMToMinutes(annonceHHMM)
  if (annonce == null || !contrat) return null
  const qui = demande?.voyageur || 'Le voyageur'
  const h = formaterHeureCourte(annonce)
  if (demande?.event_kind === EARLY || annonce >= contrat.fin) {
    return `${qui} a annoncé ${h} — vous avez sans doute plus de marge que le contrat ne le dit`
  }
  return `${qui} a annoncé ${h}`
}

// ── LES ZONES HACHURÉES ─────────────────────────────────────────────────────

/**
 * CE QUE CHAQUE VOYAGEUR GAGNERAIT, dessiné : la bande entre l'heure du contrat
 * et l'heure accordée. Elle montre le TERRAIN CÉDÉ, pas le créneau restant —
 * les deux se lisent d'un coup d'œil parce qu'ils sont côte à côte et non
 * superposés.
 *
 * @returns {{debut:number, fin:number, cote:'gauche'|'droite'}|null}
 */
export function zoneGain(demande, heureMin, contrat) {
  if (!demande || !contrat || !Number.isFinite(heureMin)) return null
  if (demande.event_kind === LATE) {
    if (heureMin <= contrat.debut) return null
    return { debut: contrat.debut, fin: heureMin, cote: 'gauche' }
  }
  if (heureMin >= contrat.fin) return null
  return { debut: heureMin, fin: contrat.fin, cote: 'droite' }
}

// ── LE MÉNAGE A-T-IL DÉJÀ COMMENCÉ ? ────────────────────────────────────────

/**
 * ⚠️ ACCORDER UN DÉPART TARDIF SUR UN MÉNAGE COMMENCÉ, C'EST FAIRE REVENIR
 * QUELQU'UN.
 *
 * L'agent est dans le logement. Repousser le départ du voyageur ne « décale »
 * pas le ménage : il l'interrompt, et impose un second déplacement que personne
 * n'a chiffré ni demandé. L'écran doit le dire AVANT, et rendre le refus plus
 * facile que l'accord — c'est le seul endroit de l'application où l'ordre des
 * boutons s'inverse, et il s'inverse pour cette raison-là.
 *
 * Un ménage VALIDÉ n'est pas « commencé » : il est fini, plus rien à
 * interrompre.
 *
 * @returns {{commence:boolean, debutMin:number|null, agent:string|null}}
 */
export function menageCommence(run, { nowMin, contrat, seuilMin } = {}) {
  const rien = { commence: false, debutMin: null, agent: null }
  if (!run || run.status === 'completed' || run.status === 'skipped') return rien
  const agent = run.assigned_provider_email || null
  if (!agent) return rien // personne n'est dedans : rien à interrompre
  const fixee = parseHHMMToMinutes(normaliserHeure(run.heure_fixee))
  const debutMin = fixee != null ? fixee : (contrat ? contrat.debut : null)
  if (debutMin == null || !Number.isFinite(nowMin)) return rien
  if (nowMin < debutMin) return rien
  // Commencé ET pas encore fini : au-delà de sa durée, il n'y a plus rien à
  // interrompre non plus — c'est un « non validé », que la frise signale déjà.
  const duree = Number.isFinite(seuilMin) ? seuilMin : DUREE_DEFAUT_MIN
  if (nowMin >= debutMin + duree) return rien
  return { commence: true, debutMin, agent }
}

/** PostgREST rend une colonne `time` en "HH:MM:SS" — on ramène à "HH:MM". */
export function normaliserHeure(v) {
  if (typeof v !== 'string') return null
  const m = v.trim().match(/^(\d{1,2}):(\d{2})/)
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : null
}

// ── LE REGROUPEMENT PAR JOURNÉE DE LOGEMENT ─────────────────────────────────

/**
 * ⚠️ UNE CARTE = UN LOGEMENT, UN JOUR. Deux demandes qui encadrent le même
 * ménage sont deux moitiés d'une seule décision : les afficher séparément
 * ferait calculer deux fois la même marge, et la seconde serait fausse.
 *
 * Les journées SANS créneau possible remontent en premier — c'est là qu'il y a
 * quelque chose à arbitrer, et une journée en conflit trois cartes plus bas se
 * découvre en défilant, c'est-à-dire trop tard.
 *
 * @returns {Array<{cle:string, logementId:string, date:string, demandes:Array}>}
 */
export function grouperParJournee(pending) {
  const par = new Map()
  for (const b of Array.isArray(pending) ? pending : []) {
    if (!b || !b.logement_id || !b.event_date) continue
    const cle = `${b.logement_id}:${b.event_date}`
    if (!par.has(cle)) {
      par.set(cle, { cle, logementId: b.logement_id, date: b.event_date, demandes: [] })
    }
    par.get(cle).demandes.push(b)
  }
  // Le départ tardif d'abord : il borne le DÉBUT du ménage, et on lit une
  // journée de gauche à droite.
  for (const j of par.values()) {
    j.demandes.sort((a, b) => (a.event_kind === LATE ? 0 : 1) - (b.event_kind === LATE ? 0 : 1))
  }
  return [...par.values()]
}

/**
 * « AUCUN CRÉNEAU POSSIBLE » — et ce que ça veut dire exactement.
 *
 * ⚠️ CE N'EST PAS « aucune valeur de curseur ne marche ». Presque toujours, en
 * n'accordant qu'une demi-heure à chacun, le ménage finit par tenir : ce test-là
 * ne signalerait jamais rien. Ce qui est impossible, c'est d'accorder CE QUI A
 * ÉTÉ DEMANDÉ — les deux voyageurs veulent des heures qui ne laissent pas le
 * temps de faire le ménage entre les deux.
 *
 * C'est cette phrase-là que le bandeau de la carte doit porter : elle dit
 * POURQUOI la journée demande un arbitrage, sans préjuger de l'arbitrage.
 *
 * ⚠️ ELLE NE BLOQUE PAS LE BOUTON. Le bouton suit le verdict COURANT : dès que
 * l'orchestrateur trouve un compromis qui tient, il doit pouvoir le valider.
 * Confondre les deux emprisonnerait la journée dans son propre diagnostic.
 */
export function journeeEnConflit({ contrat, demandes, seuilMin }) {
  if (!contrat) return true
  const list = Array.isArray(demandes) ? demandes : []
  // ⚠️ RÈGLE (Dimitri 2026-08-16) : UNE DEMANDE NON DÉCIDÉE NE CONTRAINT PAS.
  // « Impossible » se juge PAR DEMANDE, contre le contrat fourni (l'appelant y
  // a déjà intégré les FAITS : accords actés, annonces du formulaire) — jamais
  // contre l'autre pendante. Deux demandes croisées restent possibles
  // SÉPARÉMENT tant qu'aucune n'est décidée ; la contrainte n'apparaît
  // qu'après une décision (l'actée resserre le contrat au refresh).
  return list.some((d) => !bornesCurseur({ demande: d, contrat, seuilMin }).possible)
}

/** Combien de journées n'ont AUCUN créneau possible — le second chiffre du bandeau. */
export function compterJourneesEnConflit(journees) {
  return (Array.isArray(journees) ? journees : []).filter((j) => j.enConflit).length
}

/** minutes → "HH:MM" pour l'envoi au serveur (le format qu'il attend). */
export function heurePourServeur(min) {
  return formatMinutesToHHMM(min)
}
